import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  RotateCcw, Eye, LayoutGrid, SlidersHorizontal, Sparkles, Save, FolderOpen, Rocket,
  Monitor, Tablet, Smartphone, Moon, Sun, RefreshCw, GitCompare,
  Home, Store, Package, Newspaper, FileText, ShoppingCart,
} from 'lucide-react';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { cn } from '@/lib/utils';
import { SITE_NAME, IS_ECOMMERCE } from '@/config/site';
import { ALL_PRESET_IDS, LOOK_LABELS } from '@/looks';
import { presetToThemeConfig } from '@/looks/preset-seed';
import ThemeFineTuneControls from '@/components/sandbox/ThemeFineTuneControls';
import ConfirmDialog from '@/components/sandbox/ConfirmDialog';
import SandboxDraftsPanel from '@/components/sandbox/SandboxDraftsPanel';
import DesignDiff from '@/components/sandbox/DesignDiff';
import { diffDesign } from '@/components/sandbox/design-diff';
import AiThemeHelper from '@/pages/admin/components/AiThemeHelper';
import { useDesignSandboxStore } from '@/stores/design-sandbox-store';
import { useCreateDraft, usePublishDesign, useExpandDesign } from '@/hooks/useSandboxDrafts';
import { useSettings, useProducts, useBlogPosts } from '@/hooks/useProducts';
import type { DesignThemeConfig } from '@/stores/design-preview-store';
import type { GeneratedPalette } from '@/theme/apply-theme';
import {
  DESIGN_PREVIEW_APPLY,
  DESIGN_PREVIEW_READY,
  buildPreviewUrl,
} from '@/components/sandbox/preview-messages';

type SourceTab = 'gallery' | 'customize' | 'ai';
type PreviewDevice = 'desktop' | 'tablet' | 'mobile';

const DEVICE_WIDTH: Record<PreviewDevice, string> = {
  desktop: '100%',
  tablet: '820px',
  mobile: '390px',
};
const DEVICES: { id: PreviewDevice; icon: typeof Monitor; label: string }[] = [
  { id: 'desktop', icon: Monitor, label: 'Desktop' },
  { id: 'tablet', icon: Tablet, label: 'Tablet' },
  { id: 'mobile', icon: Smartphone, label: 'Mobile' },
];

const TABS: { id: SourceTab; label: string; icon: typeof LayoutGrid }[] = [
  { id: 'gallery', label: 'Gallery', icon: LayoutGrid },
  { id: 'customize', label: 'Customize', icon: SlidersHorizontal },
  { id: 'ai', label: 'AI', icon: Sparkles },
];

/**
 * A storefront page the admin can jump the preview to. `path` is the in-app
 * route (without preview flags); buildPreviewUrl adds them. Detail-page presets
 * (product/article) only appear once a representative slug resolves.
 */
type PreviewPage = { id: string; label: string; path: string; icon: typeof Home };

/** A pending publish: either publishing the candidate or reverting to a backup. */
type PublishIntent = { config: DesignThemeConfig; mode: 'publish' | 'revert' };

/**
 * Stable key for a candidate's color triple — the only inputs to palette
 * expansion. Font/look changes don't affect the generated palette, so keying on
 * just the colors lets us cache the expansion across those changes.
 */
function colorKey(c: DesignThemeConfig | null | undefined): string {
  return c ? `${c.primary_color ?? ''}|${c.accent_color ?? ''}|${c.bg_warm ?? ''}` : '';
}

/**
 * The config publishing actually writes: the candidate with the full-palette
 * opt-in stamped. Used for the preview push intent and the "what will change"
 * diffs so they reflect exactly what goes live.
 */
function withFullScope(c: DesignThemeConfig): DesignThemeConfig {
  return { ...c, palette_scope: 'full' };
}

/**
 * Design Sandbox — try alternate site design templates on your real storefront
 * without affecting the live site. Pick a Look (Gallery), fine-tune (Customize),
 * or generate one with AI; each change pushes a candidate into the embedded
 * storefront (right) via postMessage, scoped to this preview frame only. Save
 * candidates as named drafts, then explicitly Publish to go live — with a
 * one-click Revert to the auto-captured pre-publish backup. See
 * DesignPreviewBridge for the storefront-side receiver.
 */
export default function DesignSandbox() {
  const candidate = useDesignSandboxStore((s) => s.candidate);
  const setCandidate = useDesignSandboxStore((s) => s.setCandidate);
  const clearCandidate = useDesignSandboxStore((s) => s.clearCandidate);

  // On the sandbox host (not the preview iframe) useSettings returns the real
  // LIVE theme_config — the design-preview-store override only applies inside the
  // frame — so we can diff the candidate against what visitors currently see.
  const { data: settingsData } = useSettings();
  const liveConfig = settingsData?.settings?.theme_config as DesignThemeConfig | undefined;
  // The live design's editable fields — used to seed a fresh candidate so a
  // partial edit (one color/font) preserves the rest of the live design instead
  // of resetting it to defaults on publish (publish is a full replace of
  // theme_config). `generated` is derived and `palette_scope` is re-stamped on
  // publish, so both are intentionally dropped.
  const liveBaseConfig = useMemo<DesignThemeConfig | undefined>(() => {
    if (!liveConfig) return undefined;
    return {
      primary_color: liveConfig.primary_color,
      accent_color: liveConfig.accent_color,
      bg_warm: liveConfig.bg_warm,
      font_heading: liveConfig.font_heading,
      font_body: liveConfig.font_body,
      look: liveConfig.look,
    };
  }, [liveConfig]);
  // Diff what publishing would actually write (candidate + full-scope stamp)
  // against the live design, so the "Review changes" badge matches the publish
  // dialog (which also includes the full-palette opt-in).
  const candidateChanges = useMemo(
    () => (candidate ? diffDesign(liveConfig, withFullScope(candidate)) : []),
    [liveConfig, candidate],
  );

  const [tab, setTab] = useState<SourceTab>('gallery');
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [previewReady, setPreviewReady] = useState(false);
  const [device, setDevice] = useState<PreviewDevice>('desktop');
  const [previewDark, setPreviewDark] = useState(false);
  const previewDarkRef = useRef(false);
  const [previewPageId, setPreviewPageId] = useState('home');

  // Pages the admin can jump the preview to, so they can see the design across
  // the whole storefront — not just the homepage. Home + Blog exist for every
  // tenant; Shop/Product/Cart are commerce-only. The product/article detail
  // presets need a real slug, so we fetch one representative item (limit 1) and
  // only surface those presets once it loads. These run on the sandbox HOST (not
  // a preview frame), so they hit the live API normally.
  const { data: productsData } = useProducts({ limit: 1 });
  const { data: blogData } = useBlogPosts(1);
  const firstProductSlug = IS_ECOMMERCE ? (productsData?.products?.[0]?.slug as string | undefined) : undefined;
  const firstPostSlug = blogData?.posts?.[0]?.slug as string | undefined;

  const previewPages = useMemo<PreviewPage[]>(() => {
    const pages: PreviewPage[] = [{ id: 'home', label: 'Home', path: '/', icon: Home }];
    if (IS_ECOMMERCE) pages.push({ id: 'shop', label: 'Shop', path: '/shop', icon: Store });
    if (firstProductSlug) pages.push({ id: 'product', label: 'Product', path: `/product/${firstProductSlug}`, icon: Package });
    pages.push({ id: 'blog', label: 'Blog', path: '/blog', icon: Newspaper });
    if (firstPostSlug) pages.push({ id: 'article', label: 'Article', path: `/blog/${firstPostSlug}`, icon: FileText });
    if (IS_ECOMMERCE) pages.push({ id: 'cart', label: 'Cart', path: '/cart', icon: ShoppingCart });
    return pages;
  }, [firstProductSlug, firstPostSlug]);

  // The active page (falls back to Home if a once-selected detail preset's slug
  // later disappears). Its path, flagged for preview, is the iframe src — so a
  // reload always lands back on the page the admin is previewing.
  const activePage = previewPages.find((p) => p.id === previewPageId) ?? previewPages[0];
  const iframeSrc = buildPreviewUrl(activePage.path);

  // If the selected page vanishes from the list — e.g. a detail preset whose
  // representative slug was unpublished elsewhere and a background refetch
  // returned empty — snap the selection back to Home so previewPageId can't stay
  // stranded (which would yank the preview a second time if the slug reappeared).
  // activePage already falls back to Home for the render; this keeps the state in
  // sync and resets readiness so the re-handshake after that reload is clean.
  useEffect(() => {
    if (!previewPages.some((p) => p.id === previewPageId)) {
      setPreviewReady(false);
      setPreviewPageId('home');
    }
  }, [previewPages, previewPageId]);

  // Dialog / panel state
  const [showDrafts, setShowDrafts] = useState(false);
  const [showDiff, setShowDiff] = useState(false);
  const [showSave, setShowSave] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [publishIntent, setPublishIntent] = useState<PublishIntent | null>(null);
  const [status, setStatus] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  const createDraft = useCreateDraft();
  const publishDesign = usePublishDesign();
  const expandDesign = useExpandDesign();

  // The full palette (sage/brown/accent/warm/text/bg + dark) derived from the
  // candidate's colors by the backend — the same expansion the live site applies
  // on read, so the preview is faithful to what a publish produces. Cached and
  // keyed by the color triple so it's never attached to a different set of
  // colors, and so font/look changes reuse it without a round-trip.
  const generatedRef = useRef<{ key: string; generated: GeneratedPalette } | null>(null);
  // Bumped when an expansion resolves, to re-fire the push effect with the full palette.
  const [generatedVersion, setGeneratedVersion] = useState(0);

  const pushToPreview = useCallback((config: DesignThemeConfig | null) => {
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    let payload = config;
    if (config) {
      const cached = generatedRef.current;
      payload = cached && cached.key === colorKey(config)
        ? { ...config, generated: cached.generated }
        : config;
    }
    win.postMessage(
      { type: DESIGN_PREVIEW_APPLY, themeConfig: payload, dark: previewDarkRef.current },
      window.location.origin,
    );
  }, []);

  // Derive the full palette whenever the candidate's colors change (debounced).
  // Look/font-only changes keep the same color key, so the early-return reuses
  // the cached expansion with no network call. Best-effort: on failure the
  // preview still shows the base (primary/accent/warm) override.
  const candidateColorKey = colorKey(candidate);
  useEffect(() => {
    if (!candidate?.primary_color) {
      generatedRef.current = null;
      return;
    }
    if (generatedRef.current?.key === candidateColorKey) return;

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const expanded = await expandDesign.mutateAsync(candidate);
        if (cancelled) return;
        if (expanded?.generated) {
          generatedRef.current = { key: candidateColorKey, generated: expanded.generated };
          setGeneratedVersion((v) => v + 1);
        }
      } catch {
        // Expansion is best-effort; base override still applies.
      }
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // candidate is pinned by candidateColorKey; expandDesign is intentionally omitted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateColorKey]);

  const toggleDark = () => {
    const next = !previewDark;
    setPreviewDark(next);
    previewDarkRef.current = next;
    pushToPreview(candidate);
  };

  // A candidate that carries its own colour-scheme default (a dark-first
  // template) previews in that scheme, so picking Nightfall shows the night
  // sky rather than its light fallback. The admin's own toggle still works
  // afterwards; this only reacts when the candidate's default changes.
  const candidateScheme = candidate?.color_scheme_default;
  useEffect(() => {
    if (candidateScheme !== 'dark' && candidateScheme !== 'light') return;
    const next = candidateScheme === 'dark';
    if (previewDarkRef.current === next) return;
    setPreviewDark(next);
    previewDarkRef.current = next;
    pushToPreview(candidate);
    // candidate is pinned by candidateScheme on purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateScheme, pushToPreview]);

  const reloadPreview = () => {
    setPreviewReady(false);
    try {
      iframeRef.current?.contentWindow?.location.reload();
    } catch {
      // Same-origin so this is safe; guarded defensively.
    }
  };

  // Jump the preview to another storefront page. Changing the active page swaps
  // the iframe src, which reloads the frame; the bridge re-announces readiness
  // and the existing READY handler re-pushes the candidate + dark mode, so the
  // design carries across pages with no extra plumbing. Re-selecting the current
  // page (e.g. after browsing around inside the frame) force-navigates back to it.
  const selectPage = (page: PreviewPage) => {
    setPreviewReady(false);
    if (page.id === previewPageId) {
      try {
        iframeRef.current?.contentWindow?.location.assign(buildPreviewUrl(page.path));
      } catch {
        // Same-origin; guarded defensively (jsdom has no navigation).
      }
    } else {
      setPreviewPageId(page.id);
    }
  };

  // The storefront iframe announces readiness on first load and every reload.
  // Re-push the current candidate each time so the preview survives reloads.
  useEffect(() => {
    function onMessage(e: MessageEvent) {
      if (e.origin !== window.location.origin) return;
      if ((e.data as { type?: string } | null)?.type === DESIGN_PREVIEW_READY) {
        setPreviewReady(true);
        pushToPreview(useDesignSandboxStore.getState().candidate);
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [pushToPreview]);

  // Push whenever the candidate changes (or an expansion resolves), once the
  // frame is listening. generatedVersion re-fires this so the full palette lands
  // right after the debounced expansion returns.
  useEffect(() => {
    if (previewReady) pushToPreview(candidate);
  }, [candidate, previewReady, pushToPreview, generatedVersion]);

  const flash = (type: 'success' | 'error', msg: string) => {
    setStatus({ type, msg });
    setTimeout(() => setStatus(null), 4000);
  };

  const selectedPreset = candidate?.look?.preset_id;

  /** AI palette → candidate theme_config (same mapping as ThemeCustomizer).
   * Seeds from the live design so an AI color swap preserves the live fonts/Look
   * the AI result doesn't specify, rather than resetting them on publish. */
  const applyAiPalette = (palette: any, fonts?: { heading: string; body: string }) => {
    const base = candidate ?? liveBaseConfig ?? {};
    setCandidate({
      ...base,
      primary_color: palette.primary?.['500'] || base.primary_color,
      accent_color: palette.accent?.base || base.accent_color,
      bg_warm: palette.warm?.cream || base.bg_warm,
      ...(fonts?.heading && { font_heading: fonts.heading }),
      ...(fonts?.body && { font_body: fonts.body }),
    });
    setTab('customize');
  };

  const handleSaveDraft = async () => {
    if (!candidate || !draftName.trim()) return;
    try {
      await createDraft.mutateAsync({ name: draftName.trim(), config: candidate, source: 'manual' });
      setShowSave(false);
      setDraftName('');
      flash('success', 'Draft saved.');
    } catch {
      flash('error', 'Could not save draft. Please try again.');
    }
  };

  const handlePublishConfirm = async () => {
    if (!publishIntent) return;
    try {
      await publishDesign.mutateAsync(publishIntent.config);
      const wasRevert = publishIntent.mode === 'revert';
      setPublishIntent(null);
      // The candidate is now the live design — drop it so the preview reflects
      // what visitors see and the unsaved-changes actions reset.
      clearCandidate();
      flash('success', wasRevert ? 'Your live site was reverted.' : 'Your design is now live.');
    } catch {
      flash('error', 'Publish failed. Please try again.');
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Design Sandbox</h1>
          <p className="text-gray-600 mt-1">
            Try a new design on your real storefront. Changes here are a private preview — nothing
            goes live until you publish.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            onClick={() => setShowDrafts(true)}
            title="Every design you have saved, including the automatic backup taken before each publish. Open one to preview it."
          >
            <FolderOpen className="h-4 w-4 mr-2" aria-hidden="true" />
            Saved designs
          </Button>
          <Button
            variant="ghost"
            onClick={() => setShowDiff(true)}
            disabled={!candidate}
            title="Side-by-side list of exactly what differs between this candidate and the live site. Worth reading before you publish."
          >
            <GitCompare className="h-4 w-4 mr-2" aria-hidden="true" />
            Review changes
            {candidate && candidateChanges.length > 0 && (
              <span className="ml-2 inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                {candidateChanges.length}
              </span>
            )}
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowSave(true)}
            disabled={!candidate}
            title="Store this candidate under a name so you can come back to it. Saving does not publish it — visitors still see the live design."
          >
            <Save className="h-4 w-4 mr-2" aria-hidden="true" />
            Save draft
          </Button>
          <Button
            variant="outline"
            onClick={clearCandidate}
            disabled={!candidate}
            title="Throw away the candidate and go back to previewing the live design. Save it first if you want to keep it."
          >
            <RotateCcw className="h-4 w-4 mr-2" aria-hidden="true" />
            Reset to live
          </Button>
          <Button
            variant="primary"
            onClick={() =>
              candidate &&
              // Sandbox publishes opt the live site into the whole-palette swap.
              setPublishIntent({ config: withFullScope(candidate), mode: 'publish' })
            }
            disabled={!candidate}
            title="Makes this design visible to every visitor now. The current live design is saved as a backup first, so this is reversible from Saved designs."
          >
            <Rocket className="h-4 w-4 mr-2" aria-hidden="true" />
            Publish to live
          </Button>
        </div>
      </div>

      {status && (
        <div
          className={cn(
            'mb-4 p-3 rounded-lg text-sm border',
            status.type === 'success'
              ? 'bg-green-50 border-green-200 text-green-700'
              : 'bg-red-50 border-red-200 text-red-700',
          )}
          role="status"
        >
          {status.msg}
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Source picker */}
        <div className="lg:w-96 lg:flex-shrink-0">
          {/* Tabs */}
          <div className="flex gap-1 mb-3 border-b border-gray-200" role="tablist" aria-label="Design source">
            {TABS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={tab === id}
                onClick={() => setTab(id)}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
                  tab === id
                    ? 'border-green-600 text-green-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700',
                )}
              >
                <Icon className="h-4 w-4" aria-hidden="true" />
                {label}
              </button>
            ))}
          </div>

          {tab === 'gallery' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-3">
              {ALL_PRESET_IDS.map((id) => {
                const isSelected = selectedPreset === id;
                const seed = presetToThemeConfig(id);
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setCandidate(seed)}
                    className={cn(
                      'text-left p-3 rounded-lg border-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500',
                      isSelected
                        ? 'border-green-600 bg-green-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white',
                    )}
                    aria-pressed={isSelected}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="flex gap-1" aria-hidden="true">
                        <span className="h-4 w-4 rounded-full border border-black/10" style={{ backgroundColor: seed.primary_color }} />
                        <span className="h-4 w-4 rounded-full border border-black/10" style={{ backgroundColor: seed.accent_color }} />
                        <span className="h-4 w-4 rounded-full border border-black/10" style={{ backgroundColor: seed.bg_warm }} />
                      </span>
                      <span className="font-medium text-gray-900 text-sm">{LOOK_LABELS[id].label}</span>
                    </div>
                    <div className="text-xs text-gray-600 leading-snug">{LOOK_LABELS[id].description}</div>
                  </button>
                );
              })}
            </div>
          )}

          {tab === 'customize' && (
            <ThemeFineTuneControls value={candidate ?? liveBaseConfig ?? {}} onChange={setCandidate} />
          )}

          {tab === 'ai' && (
            <Card padding="none" className="overflow-hidden">
              <AiThemeHelper
                onApply={applyAiPalette}
                onClose={() => setTab('gallery')}
                context={{ storeName: SITE_NAME }}
              />
            </Card>
          )}
        </div>

        {/* Live preview of the real storefront */}
        <div className="flex-1 min-w-0">
          <Card padding="sm">
            <div className="mb-2 space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Eye className="h-4 w-4" aria-hidden="true" />
                <span>Live preview — only you can see this</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="flex items-center rounded-md border border-gray-200 overflow-hidden" role="group" aria-label="Preview device size">
                  {DEVICES.map(({ id, icon: Icon, label }) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setDevice(id)}
                      title={label}
                      aria-label={label}
                      aria-pressed={device === id}
                      className={cn('p-1.5', device === id ? 'bg-green-50 text-green-700' : 'text-gray-500 hover:bg-gray-50')}
                    >
                      <Icon className="h-4 w-4" aria-hidden="true" />
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={toggleDark}
                  title={previewDark ? 'Preview light mode' : 'Preview dark mode'}
                  aria-label={previewDark ? 'Preview light mode' : 'Preview dark mode'}
                  aria-pressed={previewDark}
                  className="p-1.5 rounded-md border border-gray-200 text-gray-500 hover:bg-gray-50"
                >
                  {previewDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                </button>
                <button
                  type="button"
                  onClick={reloadPreview}
                  title="Reload preview"
                  aria-label="Reload preview"
                  className="p-1.5 rounded-md border border-gray-200 text-gray-500 hover:bg-gray-50"
                >
                  <RefreshCw className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            </div>
            <div
              className="flex items-center gap-1 overflow-x-auto pb-0.5"
              role="group"
              aria-label="Preview page"
            >
              <span className="text-xs text-gray-500 pr-1 flex-shrink-0">View:</span>
              {previewPages.map((p) => {
                const Icon = p.icon;
                const isActive = activePage.id === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => selectPage(p)}
                    aria-pressed={isActive}
                    title={`Preview the ${p.label} page`}
                    className={cn(
                      'flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium whitespace-nowrap transition-colors flex-shrink-0',
                      isActive
                        ? 'bg-green-50 text-green-700 ring-1 ring-green-200'
                        : 'text-gray-500 hover:bg-gray-100',
                    )}
                  >
                    <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                    {p.label}
                  </button>
                );
              })}
            </div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-2 flex justify-center">
              <div
                className="bg-white rounded-md overflow-hidden shadow-sm transition-all duration-200"
                style={{ width: DEVICE_WIDTH[device], maxWidth: '100%' }}
              >
                <iframe
                  ref={iframeRef}
                  src={iframeSrc}
                  title="Storefront design preview"
                  className="w-full h-[72vh] block"
                />
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Saved designs drawer */}
      {showDrafts && (
        <SandboxDraftsPanel
          onClose={() => setShowDrafts(false)}
          onLoad={(config) => {
            setCandidate(config);
            setShowDrafts(false);
          }}
          onRevert={(config) => {
            setShowDrafts(false);
            setPublishIntent({ config, mode: 'revert' });
          }}
        />
      )}

      {/* Read-only "what's different vs live" review */}
      {showDiff && (
        <ConfirmDialog
          title="Changes vs your live design"
          confirmLabel="Done"
          hideCancel
          onConfirm={() => setShowDiff(false)}
          onCancel={() => setShowDiff(false)}
        >
          <DesignDiff changes={candidateChanges} />
        </ConfirmDialog>
      )}

      {/* Save-draft name prompt */}
      {showSave && (
        <ConfirmDialog
          title="Save this design as a draft"
          confirmLabel="Save"
          confirmDisabled={!draftName.trim()}
          busy={createDraft.isPending}
          onConfirm={handleSaveDraft}
          onCancel={() => setShowSave(false)}
        >
          <Input
            label="Draft name"
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            placeholder="e.g. Spring refresh"
            autoFocus
          />
        </ConfirmDialog>
      )}

      {/* Publish / revert confirmation */}
      {publishIntent && (
        <ConfirmDialog
          title={publishIntent.mode === 'revert' ? 'Revert your live site?' : 'Publish to your live site?'}
          confirmLabel={publishIntent.mode === 'revert' ? 'Revert' : 'Publish'}
          busy={publishDesign.isPending}
          onConfirm={handlePublishConfirm}
          onCancel={() => setPublishIntent(null)}
        >
          <div className="mb-4">
            <div className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-2">
              This will change
            </div>
            <DesignDiff changes={diffDesign(liveConfig, publishIntent.config)} />
          </div>
          {publishIntent.mode === 'revert' ? (
            <>This re-publishes the backup snapshot to your live site. Every visitor will see it.</>
          ) : (
            <>
              This replaces the design every visitor sees on your live site. We’ll save a backup of
              your current live design first so you can revert.
            </>
          )}
        </ConfirmDialog>
      )}
    </div>
  );
}
